import { createColumnHelper } from "@tanstack/react-table";
import classNames from "components/ui/classNames";
import BrazilianCompanyIssuerListingSegment from "components/company/v2/BrazilianCompanyIssuerListingSegment";
import ExecutivesOrDirectorSheetContent from "components/company/sections/collapsible/ExecutivesOrDirectorSheetContent";
import BrazilianCompanyIssuerFreeFloat from "components/company/v2/BrazilianCompanyIssuerFreeFloat";
import BrazilianCompanyIssuerTagAlong from "components/company/v2/BrazilianCompanyIssuerTagAlong";
import BrazilianCompanyMajorityShareholders from "components/company/v2/BrazilianCompanyMajorityShareholders";
import { Button } from "components/ui/Button";
import { Sheet } from "components/ui/Sheet";
import Table from "components/ui/Table";
import { TableBreakpoint } from "components/ui/Table/interface";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "components/ui/Tabs";
import conditionalCounters from "components/utils/conditionalCounters";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import { Dialog, DialogCloseX, palette, shades } from "lib/miolo";
import orderBy from "lodash/fp/orderBy";
import trimEnd from "lodash/fp/trimEnd";
import numeral from "numeral";
import type { ReactNode } from "react";
import { useState } from "react";
import type { IBrazilianCompanyIssuerTreasuryShares } from "types/graphqlTypes";
import { IBrazilianCompanyIssuerShareholderType } from "types/graphqlTypes";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import { format } from "date-fns";
import parseDateYmd from "lib/utils/parseDateYmd";

function Title({ children }: { children: ReactNode }) {
  return <h6 className="flex font-aeonik text-sm font-semibold uppercase tracking-wide text-slate-600">{children}</h6>;
}

interface ICompanyIssuerGovernanceProps {
  issuer:
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment
    | IAmericanCompanyIssuerTopLevelFragment;
}

export default function CompanyIssuerGovernance({ issuer }: ICompanyIssuerGovernanceProps) {
  const selfie = useSelfie();
  const [showAllShareholders, setShowAllShareholders] = useState(false);
  const [isChiefExecutiveOfficerSheetOpen, setIsChiefExecutiveOfficerSheetOpen] = useState(false);
  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";
  const isBrazilianRealEstateFundIssuer = issuer.__typename === "BrazilianRealEstateFund";
  const isAmericanIssuer = issuer.__typename === "AmericanCompanyIssuer";

  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-6 font-aeonik">
      <div className="space-y-6">
        {isAmericanIssuer && issuer.chiefExecutiveOfficer && (
          <div className="space-y-2">
            <Title>CEO</Title>

            <Sheet
              open={isChiefExecutiveOfficerSheetOpen}
              onOpenChange={() => setIsChiefExecutiveOfficerSheetOpen(false)}
            >
              <ExecutivesOrDirectorSheetContent issuer={issuer} person={issuer.chiefExecutiveOfficer} />
            </Sheet>

            {selfie ? (
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => {
                  setIsChiefExecutiveOfficerSheetOpen(true);
                }}
              >
                {issuer.chiefExecutiveOfficer.fullName}
              </Button>
            ) : (
              <span className="flex text-sm font-medium text-gray-500 xl:text-base">
                {issuer.chiefExecutiveOfficer.fullName}
              </span>
            )}
          </div>
        )}
        {isBrazilianIssuer && issuer.chiefExecutiveOfficer && (
          <div className="space-y-2">
            <Title>CEO</Title>
            <span className="flex text-sm font-medium text-gray-500 xl:text-base">
              {issuer.chiefExecutiveOfficer.name}
            </span>
          </div>
        )}

        {isBrazilianIssuer && (
          <div className="flex flex-col justify-between space-y-6 xl:flex-row xl:space-y-0">
            {issuer.listingSegment && (
              <div className="space-y-2">
                <Title>Segmento de Listagem</Title>
                <BrazilianCompanyIssuerListingSegment listingSegment={issuer.listingSegment} />
              </div>
            )}

            {issuer.tagAlong && (
              <div className="space-y-2">
                <Title>Tag Along</Title>
                <BrazilianCompanyIssuerTagAlong tagAlong={issuer.tagAlong} />
              </div>
            )}

            {issuer.freeFloat && (
              <div className="space-y-2">
                <Title>Free Float</Title>
                <BrazilianCompanyIssuerFreeFloat freeFloat={issuer.freeFloat} />
              </div>
            )}
          </div>
        )}

        {isBrazilianIssuer && (
          <div className="space-y-2">
            <Title>{issuer.majorityShareholders.length === 1 ? "Sócio Majoritário" : "Sócios Majoritários"}</Title>

            <BrazilianCompanyMajorityShareholders majorityShareholders={issuer.majorityShareholders} />

            {showAllShareholders && (
              <AllShareholdersDialog
                isOpen={showAllShareholders}
                onDismiss={() => setShowAllShareholders(false)}
                positionOfShareholders={issuer.positionOfShareholders}
                treasuryShares={issuer.treasuryShares}
                ownershipStructureUrl={issuer.ownershipStructureUrl}
              />
            )}

            <div className="hidden pt-2 xl:block">
              <button
                type="button"
                onClick={() => setShowAllShareholders(true)}
                className="font-aeonik text-sm font-bold text-blue-600 hover:underline"
              >
                Ver todos os sócios ({issuer.positionOfShareholders.length}) →
              </button>
            </div>
          </div>
        )}

        {isBrazilianRealEstateFundIssuer && (
          <div className="grid grid-cols-3 gap-y-6 xl:flex-row xl:space-y-0">
            <div className="space-y-2">
              <Title>Prazo</Title>
              <span className="text-sm font-medium text-gray-500 xl:text-base">
                {issuer.dateOfDuration ? format(parseDateYmd(issuer.dateOfDuration), "dd/MM/yyyy") : "Indeterminado"}
              </span>
            </div>

            <div className="col-span-2 space-y-2">
              <Title>Público-alvo</Title>
              <span className="text-sm font-medium text-gray-500 xl:text-base">{issuer.audience}</span>
            </div>

            {issuer.selfRegulation.primarySegment && (
              <div className="space-y-2">
                <Title>Segmento</Title>
                <span className="text-sm font-medium text-gray-500 xl:text-base">
                  {issuer.selfRegulation.primarySegment}
                </span>
              </div>
            )}

            {issuer.classification.length > 0 && (
              <div className="space-y-2">
                <Title>Tipo</Title>
                <span className="text-sm font-medium text-gray-500 xl:text-base">
                  {issuer.classification.join(" / ")}
                </span>
              </div>
            )}

            {issuer.selfRegulation.mandate && (
              <div className="space-y-2">
                <Title>Mandato</Title>
                <span className="text-sm font-medium text-gray-500 xl:text-base">{issuer.selfRegulation.mandate}</span>
              </div>
            )}

            {issuer.managementReport && (
              <div className="col-span-3 space-y-2">
                <Title>Relatório Gerencial</Title>
                <a
                  title="Relatório Gerencial"
                  target="_blank"
                  href={issuer.managementReport.linkHref}
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-gray-500 no-underline hover:underline xl:text-base"
                >
                  Referência: {format(parseDateYmd(issuer.managementReport.dateOfReference), "dd/MM/yyyy")}
                </a>
              </div>
            )}

            {issuer.selfRegulation.management && (
              <div className="space-y-2">
                <Title>Gestão</Title>
                <span className="text-sm font-medium text-blue-600 xl:text-base">
                  {issuer.selfRegulation.management}
                </span>
              </div>
            )}

            {issuer.manager && (
              <div className="space-y-2">
                <Title>Gestor</Title>
                <span className="text-sm font-medium text-gray-500 xl:text-base">{issuer.manager.name}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface IAllShareholdersDialogProps {
  ownershipStructureUrl: IBrazilianCompanyIssuerTopLevelFragment["ownershipStructureUrl"];
  positionOfShareholders: IBrazilianCompanyIssuerTopLevelFragment["positionOfShareholders"];
  treasuryShares: IBrazilianCompanyIssuerTreasuryShares | null;
  isOpen: boolean;
  onDismiss: () => void;
}

function AllShareholdersDialog({
  ownershipStructureUrl,
  positionOfShareholders,
  treasuryShares,
  isOpen,
  onDismiss,
}: IAllShareholdersDialogProps) {
  const [isShowingAllShareholders, setShowAllShareholders] = useState(false);

  const treasurySharesColumns = getTreasurySharesColumns(treasuryShares);
  const hasTreasuryShares = treasuryShares && treasuryShares.totalShares > 0;

  const shareholdersCount = conditionalCounters(positionOfShareholders, {
    individualShareholders: ({ type }) => type === IBrazilianCompanyIssuerShareholderType.Individual,
    corporationShareholders: ({ type }) => type === IBrazilianCompanyIssuerShareholderType.Corporation,
    otherShareholders: ({ type }) => type === IBrazilianCompanyIssuerShareholderType.Other,
  });

  const minorityShareholdersCount = positionOfShareholders.filter((node) => {
    return node.percentageOfTotalShares < 0.5;
  }).length;

  const shareholders = positionOfShareholders.filter((node) => {
    if (isShowingAllShareholders) {
      return true;
    }
    return node.percentageOfTotalShares > 0.5;
  });

  return (
    <Dialog
      aria-label="Socios da empresa"
      isOpen={isOpen}
      onDismiss={onDismiss}
      allowPinchZoom={true}
      className={classNames(
        "relative m-3 box-border block min-w-[320px] max-w-screen-legacyDesktop rounded p-5",
        "sm:mx-auto sm:my-[10vh] lg:rounded-xl lg:p-6",
      )}
    >
      <div className="grid gap-y-5">
        <header>
          <div className="flex items-start justify-between">
            <h1 className="m-0 text-3xl font-bold tracking-wider text-[#172a41]">Sócios</h1>

            <DialogCloseX onClick={onDismiss} />
          </div>

          <p className="mb-3 mt-1 text-sm font-medium tracking-wider text-[#627d98]">
            Relação com todos os sócios reportados pela empresa na CVM.
          </p>

          <a
            target="_blank"
            rel="noopener noreferrer"
            href={ownershipStructureUrl}
            className="text-sm font-medium tracking-wider text-blue-800 no-underline hover:underline"
          >
            Ver organograma dos acionistas →
          </a>
        </header>

        <div className="-mx-6">
          <hr className="m-0 border-t border-neutral-200" />

          <div className="p-3">
            <Tabs>
              <TabList>
                <Tab>Total ({positionOfShareholders.length})</Tab>
                <Tab disabled={shareholdersCount.individualShareholders === 0}>
                  Pessoa Física ({shareholdersCount.individualShareholders})
                </Tab>
                <Tab disabled={shareholdersCount.corporationShareholders === 0}>
                  Pessoa Jurídica ({shareholdersCount.corporationShareholders})
                </Tab>
                <Tab disabled={shareholdersCount.otherShareholders === 0}>
                  Outros ({shareholdersCount.otherShareholders})
                </Tab>
                {hasTreasuryShares && <Tab>Ações em Tesouraria</Tab>}
              </TabList>

              <TabPanels>
                <TabPanel>
                  <ShareholdersTable shareholders={shareholders} />

                  {minorityShareholdersCount > 0 && (
                    <div className="py-3 text-center">
                      <button
                        type="button"
                        onClick={() => setShowAllShareholders(!isShowingAllShareholders)}
                        className="m-0 cursor-pointer appearance-none border-none bg-none p-0 text-sm tracking-wider text-blue-800"
                      >
                        {isShowingAllShareholders
                          ? `Recolher sócios minoritários`
                          : `Ver os demais sócios (${minorityShareholdersCount})`}
                      </button>
                    </div>
                  )}
                </TabPanel>
                <TabPanel>
                  <ShareholdersTable
                    shareholders={positionOfShareholders.filter(({ type }) => {
                      return type === IBrazilianCompanyIssuerShareholderType.Individual;
                    })}
                  />
                </TabPanel>
                <TabPanel>
                  <ShareholdersTable
                    shareholders={positionOfShareholders.filter(({ type }) => {
                      return type === IBrazilianCompanyIssuerShareholderType.Corporation;
                    })}
                  />
                </TabPanel>
                <TabPanel>
                  <ShareholdersTable
                    shareholders={positionOfShareholders.filter(({ type }) => {
                      return type === IBrazilianCompanyIssuerShareholderType.Other;
                    })}
                  />
                </TabPanel>
                <TabPanel>
                  <Table<IBrazilianCompanyIssuerTreasuryShares>
                    columns={treasurySharesColumns}
                    data={treasuryShares ? [treasuryShares] : []}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

interface IShareholdersTableProps {
  shareholders: IBrazilianCompanyIssuerTopLevelFragment["positionOfShareholders"];
}

function ShareholdersTable({ shareholders }: IShareholdersTableProps) {
  const hasShareholderWithPreferredStock = shareholders.some((shareholder) => {
    return shareholder.percentageOfTotalSharesOfPreferredStock > 0;
  });

  return (
    <div className="overflow-hidden rounded bg-white p-2">
      <table className="min-w-full border-collapse border-inherit text-left indent-0 text-sm tracking-wide">
        <thead className="bg-white">
          <tr>
            <th className="px-4 py-3 font-semibold uppercase text-neutral-700">Nome</th>
            <th className="px-4 py-3 text-right font-semibold uppercase text-neutral-700">% ON</th>
            {hasShareholderWithPreferredStock && (
              <th className="px-4 py-3 text-right font-semibold uppercase text-neutral-700">% PN</th>
            )}
          </tr>
        </thead>

        <tbody>
          {orderBy(["percentageOfTotalShares"], ["desc"], shareholders).map((node, index) => {
            return (
              <tr
                key={`${node.name}/${String(index)}`}
                className={classNames(
                  "group border-t border-neutral-200",
                  "hover:border-t-transparent [&:hover+tr]:border-t-transparent",
                )}
              >
                <td
                  className={classNames(
                    "px-4 py-3 group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <div className="flex items-center">
                    <div className="w-[280px]">
                      <div>
                        <span className="text-base font-medium text-[#394e66]">{node.name}</span>
                      </div>

                      <div>
                        <span className="text-sm tracking-wider text-[#4e657f]">
                          {"Nacionalidade: "}
                          <strong className="text-[#627d98]">{node.nationality}</strong>
                        </span>
                      </div>
                    </div>

                    {node.isControllingShareholder && (
                      <div className="ml-3">
                        <span className="rounded-xl bg-[#333333] px-3 py-1 font-medium tracking-wider text-white">
                          Controlador
                        </span>
                      </div>
                    )}

                    {node.isGovernmentOrGovernmentRelated && (
                      <div className="ml-3">
                        <span className="rounded-xl bg-[#f6dfdf] px-3 py-1 font-medium tracking-wider text-[#751f1f]">
                          Governo
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td
                  className={classNames(
                    "px-4 py-3 text-right group-hover:bg-neutral-50",
                    "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                  )}
                >
                  <div>
                    <span
                      className="text-base font-bold"
                      style={{
                        color: palette.oceanGreen,
                      }}
                    >
                      {node.percentageOfTotalSharesOfCommonStock.toFixed(2).replace(".00", "").replace(".", ",")}%
                    </span>
                  </div>

                  {node.percentageOfTotalSharesOfCommonStock > 0.5 && (
                    <div>
                      <span
                        className="text-xs font-medium tracking-wider"
                        style={{
                          color: shades.greys.variants[4],
                        }}
                      >
                        {numeral(node.totalSharesOfCommonStock).format("0,0")} ações
                      </span>
                    </div>
                  )}
                </td>
                {hasShareholderWithPreferredStock && (
                  <td
                    className={classNames(
                      "px-4 py-3 text-right group-hover:bg-neutral-50",
                      "group-hover:first-of-type:rounded-l-md group-hover:last-of-type:rounded-r-md",
                    )}
                  >
                    <div>
                      <span
                        className="text-base font-bold"
                        style={{
                          color: palette.copper,
                        }}
                      >
                        {node.percentageOfTotalSharesOfPreferredStock.toFixed(2).replace(".00", "").replace(".", ",")}%
                      </span>
                    </div>

                    {node.percentageOfTotalSharesOfPreferredStock > 0.5 && (
                      <div>
                        <span
                          className="text-xs font-medium tracking-wider"
                          style={{
                            color: shades.greys.variants[4],
                          }}
                        >
                          {numeral(node.totalSharesOfPreferredStock).format("0,0")} ações
                        </span>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ITreasurySharesCellProps {
  percentageOfTotal: number;
  shares: number;
}

function TreasurySharesCell({ percentageOfTotal, shares }: ITreasurySharesCellProps) {
  return (
    <div>
      <div>
        <span className="text-base font-bold text-emerald-600">
          {percentageOfTotal < 0.01
            ? trimEnd(String(percentageOfTotal).replace(".", ","))
            : percentageOfTotal.toFixed(2).replace(".00", "").replace(".", ",")}
          %
        </span>
      </div>

      <div>
        <span className="text-medium text-xs tracking-wider text-gray-600">{numeral(shares).format("0,0")} ações</span>
      </div>
    </div>
  );
}

function getTreasurySharesColumns(treasuryShares: IBrazilianCompanyIssuerTreasuryShares | null) {
  if (!treasuryShares) {
    return [];
  }

  const columnHelper = createColumnHelper<IBrazilianCompanyIssuerTreasuryShares>();
  const hiddenPreferredStock = !treasuryShares.totalSharesOfPreferredStock;
  const hiddenCommonStock = !treasuryShares.totalSharesOfCommonStock;

  const headerClassName = classNames(
    "border-b border-gray-200 py-3 px-4",
    "text-left text-sm font-semibold uppercase tracking-wide text-gray-700",
  );
  const cellClassName = classNames("text-left text-sm tracking-wide text-gray-700");

  const arrayOfColumns = [
    columnHelper.accessor("totalSharesOfCommonStock", {
      header: "% ON",
      cell: (props) => (
        <TreasurySharesCell
          percentageOfTotal={props.row.original.percentageOfTotalSharesOfCommonStock}
          shares={props.getValue()}
        />
      ),
      meta: {
        isHidden: hiddenCommonStock,
        header: {
          className: headerClassName,
        },
        cell: {
          className: cellClassName,
        },
      },
    }),
    columnHelper.accessor("totalSharesOfPreferredStock", {
      header: "% PN",
      cell: (props) => (
        <TreasurySharesCell
          percentageOfTotal={props.row.original.percentageOfTotalSharesOfPreferredStock}
          shares={props.getValue()}
        />
      ),
      meta: {
        isHidden: hiddenPreferredStock,
        breakpoint: TableBreakpoint.Small,
        header: {
          className: headerClassName,
        },
        cell: {
          className: cellClassName,
        },
      },
    }),
    columnHelper.accessor("totalShares", {
      header: "TOTAL",
      cell: (props) => (
        <TreasurySharesCell percentageOfTotal={props.row.original.percentageOfTotalShares} shares={props.getValue()} />
      ),
      meta: {
        breakpoint: TableBreakpoint.Small,
        header: {
          className: headerClassName,
        },
        cell: {
          className: cellClassName,
        },
      },
    }),
  ];

  return arrayOfColumns;
}
